import { CoreFetch, ReportApi } from '@enter/core';
import * as QuickSightEmbedding from 'amazon-quicksight-embedding-sdk';
import { ContactSupport, PageLayoutRow } from 'components';
import { inject, observer } from 'mobx-react';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { IInjectedRootStore, stream2string } from 'stores';
import * as S from './reports.styles';

type IReportsProps = IInjectedRootStore;

export const Reports = inject('rootStore')(
    observer((props: IReportsProps) => {
        const { rootStore } = props;

        const [loading, setLoading] = useState(true);
        const [unavailable, setUnavailable] = useState(false);
        const [error, setError] = useState(false);

        document.title = 'Provider Dashboard - Reports';

        useLayoutEffect(() => {
            // Hide sidebar when open this page
            rootStore.layoutStore.toggleSidebar(true);
        }, []);

        useEffect(() => {
            (async (): Promise<void> => {
                try {
                    setLoading(true);
                    const { url } = await CoreFetch<ReportApi>(ReportApi).getProviderQuicksightUrl();
                    const options = {
                        url,
                        container: '#dashboardContainer',
                        parameters: {},
                        scrolling: 'auto',
                    };
                    const dashboard = QuickSightEmbedding.embedDashboard(options);
                    dashboard.on('load', () => {
                        setError(false);
                    });
                    dashboard.on('error', () => {
                        setError(true);
                    });
                    setLoading(false);
                } catch (err) {
                    // tslint:disable-next-line: no-console
                    console.error(err);

                    if (err && err.body) {
                        const str = await stream2string(err.body);
                        const msg = JSON.parse(str);
                        if (msg.code === 'err.quicksight_configuration_does_not_exist') {
                            setUnavailable(true);
                        } else {
                            setError(true);
                        }
                    } else {
                        setError(true);
                    }
                    setLoading(false);
                }
            })();
        }, []);

        const renderLoader = (): React.ReactElement => <S.Loader />;
        const renderUnavailable = (): React.ReactElement => (
            <S.Message>
                Sorry! Your account is not configured for reports.
                <ContactSupport action="request access to reports" subject="Requesting access to reports" />
            </S.Message>
        );
        const renderError = (): React.ReactElement => (
            <S.Message>
                Sorry! An error occurred while loading your reports.
                <ContactSupport action="request help or refresh to try again" subject="Need help with reports" />
            </S.Message>
        );
        const renderConditional = (): React.ReactElement | undefined => {
            if (loading) {
                return renderLoader();
            } else if (unavailable) {
                return renderUnavailable();
            } else if (error) {
                return renderError();
            }
        };

        return (
            <S.Wrapper>
                <PageLayoutRow expand={!unavailable}>
                    {renderConditional()}
                    {!unavailable && <S.Embedded id="dashboardContainer" />}
                </PageLayoutRow>
            </S.Wrapper>
        );
    }),
);
